import React, { useState, useEffect, useCallback } from 'react';
import { format, parseISO, subDays, startOfWeek, endOfWeek, getISOWeek, getMonth, getYear, startOfMonth, endOfMonth, subMonths as dateFnsSubMonths, subWeeks as dateFnsSubWeeks, subYears, endOfYear } from 'date-fns';
import { es } from 'date-fns/locale';
import './ReporteKPI.css';

// Helper function to get the authentication token
const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// Helper component for rendering KPI change values
function KpiChange({ value }) {
    if (value === null || isNaN(value)) {
        return <span className="kpi-change neutral">-</span>;
    }
    const isPositive = value >= 0;
    const arrow = isPositive ? '▲' : '▼';
    const className = isPositive ? 'positive' : 'negative';
    const sign = isPositive ? '+' : '';
    return <span className={className}>{sign}{value.toFixed(1)}% {arrow}</span>;
}

function ReporteKPI() {
    const [activeTab, setActiveTab] = useState('Diaria');
    const [selectedDate, setSelectedDate] = useState(format(subDays(new Date(), 1), 'yyyy-MM-dd'));
    const [selectedCompsetId, setSelectedCompsetId] = useState('');
    const [compsets, setCompsets] = useState([]);
    
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // 1. ADD USER LEVEL STATE
    const [userLevel, setUserLevel] = useState('');

    // 2. GET USER LEVEL ON LOAD
    useEffect(() => {
        const userString = localStorage.getItem('user');
        if (userString) {
            try {
                const user = JSON.parse(userString);
                setUserLevel(user.user_level);
            } catch (e) {
                console.error("Failed to parse user from localStorage", e);
            }
        }
    }, []);

    // This effect updates the default date when the user switches tabs
    useEffect(() => {
        const today = new Date();
        let newDefaultDateString = '';
        switch (activeTab) {
            case 'Diaria':
                newDefaultDateString = format(subDays(today, 1), 'yyyy-MM-dd');
                break;
            case 'Semanal':
                const endOfLastWeek = subDays(startOfWeek(today, { weekStartsOn: 1 }), 0);
                newDefaultDateString = format(endOfLastWeek, 'yyyy-MM-dd');
                break;
            case 'Mensual':
                const firstOfThisMonth = startOfMonth(today);
                const endOfLastMonth = subDays(firstOfThisMonth, 1);
                newDefaultDateString = format(endOfLastMonth, 'yyyy-MM-dd');
                break;
            case 'Anual':
                const endOfLastYear = endOfYear(subYears(today, 1));
                newDefaultDateString = format(endOfLastYear, 'yyyy-MM-dd');
                break;
        }
        if(newDefaultDateString) {
            setSelectedDate(newDefaultDateString);
        }
    }, [activeTab]);

    const fetchCompsets = useCallback(async () => {
        // Wait for userLevel to be determined
        if (!userLevel) {
             const userString = localStorage.getItem('user');
             if(userString) {
                 const u = JSON.parse(userString);
                 if(u.user_level) return; // Wait for the state to update in next render or continue below
             }
        }

        setLoading(true);
        setError(null);

        const userString = localStorage.getItem('user');
        const user = userString ? JSON.parse(userString) : null;
        const currentUserLevel = user ? user.user_level : '';

        // --- THE FIX: MIRRORING REPORTECLASICO LOGIC ---
        if (currentUserLevel === 'Ejecutivo (Restringido)') {
            // Logic for Restricted Users: DO NOT CALL API FOR COMPSETS
            const userDefaultCompSetId = user ? user.default_compset_id : null;
            
            if (userDefaultCompSetId) {
                setSelectedCompsetId(userDefaultCompSetId);
                // We mock a single compset so logic doesn't break, though the selector will be hidden
                setCompsets([{ comp_set_id: userDefaultCompSetId, name: 'Default' }]);
            } else {
                setError("No se encontró un compset por defecto para su usuario.");
                setLoading(false);
            }
            // We stop here for restricted users.
            return;
        } 
        
        // Logic for ALL OTHER USERS (Call the API)
        try {
            const apiUrl = `${process.env.REACT_APP_API_URL}/api.php?path=compsets`;
            const response = await fetch(apiUrl, { headers: getAuthHeader() });

            if (!response.ok) throw new Error('Failed to fetch compsets');
            
            const data = await response.json();
            
            if (data.success && Array.isArray(data.data)) {
                const fetchedCompsets = data.data;
                setCompsets(fetchedCompsets);
                if (fetchedCompsets.length > 0) {
                    const userDefaultCompSetId = user ? user.default_compset_id : null;
                    const defaultExists = userDefaultCompSetId ? fetchedCompsets.some(c => c.comp_set_id == userDefaultCompSetId) : false;
                    setSelectedCompsetId(defaultExists ? userDefaultCompSetId : fetchedCompsets[0].comp_set_id);
                }
            } else {
                throw new Error(data.error?.message || "Invalid compset data");
            }
        } catch (err) {
            setError(err.message);
        } finally {
            // Main loading state is handled by report fetch, but if we error here we need to stop
        }
    }, [userLevel]); // Depend on userLevel so it runs once we know who they are

    useEffect(() => {
        fetchCompsets();
    }, [fetchCompsets]);

    const fetchReportData = useCallback(async () => {
        if (!selectedCompsetId || !selectedDate) return;
        setLoading(true);
        setError(null);
        try {
            const currentParsedDate = parseISO(selectedDate);
            const queryParams = new URLSearchParams({
                type: activeTab.toLowerCase(),
                compsetId: selectedCompsetId,
                date: selectedDate,
                month: (getMonth(currentParsedDate) + 1).toString(),
                year: getYear(currentParsedDate).toString()
            });
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api.php?path=reports/kpi&${queryParams.toString()}`, { headers: getAuthHeader() });
            if (!response.ok) {
                 const errorBody = await response.json().catch(() => ({error: {message: "An unknown server error occurred."}}));
                 throw new Error(errorBody.error?.message || `HTTP error! Status: ${response.status}`);
            }
            const data = await response.json();
            if (data.success) {
                setReportData(data.data);
            } else {
                throw new Error(data.error?.message || 'Failed to fetch report data');
            }
        } catch (err) {
            setError(err.message);
            setReportData(null);
        } finally {
            setLoading(false);
        }
    }, [activeTab, selectedDate, selectedCompsetId]);

    useEffect(() => {
        if (selectedCompsetId) {
            fetchReportData();
        }
    }, [fetchReportData, selectedCompsetId]);

    const formatValue = (metric, value) => {
        if (value === null || value === '') return '-';
        const numValue = parseFloat(value);
        if (isNaN(numValue)) return 'N/A';
        switch (metric) {
            case 'Occupancy': return `${numValue.toFixed(1)}%`;
            case 'ADR':
            case 'RevPAR': return `$${numValue.toFixed(2)}`;
            default: return numValue.toFixed(0);
        }
    };

    const generateWeekOptions = () => {
        const options = [];
        let current = new Date();
        let endOfLastWeek = subDays(startOfWeek(current, { weekStartsOn: 1 }), 0); 
        for (let i = 0; i < 52; i++) {
            const weekEndDate = endOfLastWeek;
            const weekStartDate = startOfWeek(weekEndDate, { weekStartsOn: 1 });
            const weekNum = getISOWeek(weekEndDate);
            const label = `Semana ${weekNum} (${format(weekStartDate, 'dd MMM', { locale: es })} - ${format(weekEndDate, 'dd MMM yy', { locale: es })})`;
            options.push(<option key={i} value={format(weekEndDate, 'yyyy-MM-dd')}>{label}</option>);
            endOfLastWeek = subDays(weekStartDate, 1);
        }
        return options;
    };
    
    const generateMonthOptions = () => {
        const options = [];
        let currentMonth = startOfMonth(new Date());
        for (let i = 0; i < 24; i++) {
            const label = format(currentMonth, 'MMMM yyyy', { locale: es });
            const value = format(endOfMonth(currentMonth), 'yyyy-MM-dd'); 
            options.push(<option key={i} value={value}>{label}</option>);
            currentMonth = dateFnsSubMonths(currentMonth, 1);
        }
        return options;
    };

    const generateYearOptions = () => {
        const options = [];
        const currentYear = getYear(new Date());
        for (let i = 0; i < 10; i++) {
            const year = currentYear - i;
            const value = format(endOfYear(new Date(year, 0, 1)), 'yyyy-MM-dd');
            options.push(<option key={year} value={value}>{year}</option>);
        }
        return options;
    };

    const renderHeader = () => {
        if (!reportData || !reportData.dates_this_year) return null;
        return (
            <tr>
                <th rowSpan="2">Entidad</th>
                {reportData.dates_this_year.map((dateStr, index) => {
                    const dateTY = parseISO(dateStr);
                    const dateLY = parseISO(reportData.dates_last_year[index]);
                    let thisYearLabel, lastYearLabel;
                    if (activeTab === 'Semanal') {
                        thisYearLabel = `Semana ${getISOWeek(dateTY)} '${getYear(dateTY).toString().substring(2)}`;
                        lastYearLabel = `Semana ${getISOWeek(dateLY)} '${getYear(dateLY).toString().substring(2)}`;
                    } else if (activeTab === 'Mensual') {
                        thisYearLabel = format(dateTY, 'MMM yy', { locale: es });
                        lastYearLabel = format(dateLY, 'MMM yy', { locale: es });
                    } else if (activeTab === 'Anual') {
                        thisYearLabel = `Año ${getYear(dateTY)}`;
                        lastYearLabel = `Año ${getYear(dateLY)}`;
                    } else {
                        thisYearLabel = format(dateTY, 'eee dd-MM-yy', { locale: es });
                        lastYearLabel = format(dateLY, 'eee dd-MM-yy', { locale: es });
                    }
                    return (
                        <th colSpan="2" key={dateStr}>
                            <div className="date-header-ty">{thisYearLabel}</div>
                            <div className="date-header-ly">{lastYearLabel}</div>
                        </th>
                    )
                })}
            </tr>
        );
    };

    const renderSubHeader = () => {
        if (!reportData || !reportData.dates_this_year) return null;
        return (
            <tr className="subheader">
                {reportData.dates_this_year.map(dateStr => (
                    <React.Fragment key={dateStr}>
                        <th>Valor</th>
                        <th>% Cambio</th>
                    </React.Fragment>
                ))}
            </tr>
        );
    };
    
    const renderTableBody = () => {
        if (!reportData || !reportData.metrics) return null;
        const metricOrder = ['Occupancy', 'ADR', 'RevPAR', 'MPI', 'ARI', 'RGI'];
        return (
            <tbody>
                {metricOrder.map(metricName => {
                    const metricData = reportData.metrics[metricName];
                    if (!metricData) return null;
                    const isIndexMetric = ['MPI', 'ARI', 'RGI'].includes(metricName);
                    return (
                        <React.Fragment key={metricName}>
                            <tr className="kpi-metric-header"><th colSpan={(reportData.dates_this_year.length * 2) + 1}>{metricName}</th></tr>
                            <tr>
                                <td>Mi Propiedad</td>
                                {metricData.propiedad?.map((day, index) => (<React.Fragment key={`prop-${index}`}><td>{formatValue(metricName, day.value)}</td><td><KpiChange value={day.change} /></td></React.Fragment>))}
                            </tr>
                            {!isIndexMetric && (
                                <tr>
                                    <td>Compset Average</td>
                                    {metricData.compset?.map((day, index) => (<React.Fragment key={`comp-${index}`}><td>{formatValue(metricName, day.value)}</td><td><KpiChange value={day.change} /></td></React.Fragment>))}
                                </tr>
                            )}
                        </React.Fragment>
                    );
                })}
            </tbody>
        );
    };

    // 3. DEFINE RESTRICTED USER BOOL FOR UI
    const isRestrictedUser = userLevel === 'Ejecutivo (Restringido)';

    return (
        <div className="reporte-kpi-container">
            <header className="main-header">
                <h1 className="page-title">Reporte de KPIs</h1>
            </header>
            <section className="info-entry-section report-tabs-section">
                <div className="report-tabs">
                    {['Diaria', 'Semanal', 'Mensual', 'Anual'].map(tabName => (
                        <button key={tabName} className={`report-tab-button ${activeTab === tabName ? 'active' : ''}`} onClick={() => setActiveTab(tabName)}>
                            {tabName}
                        </button>
                    ))}
                </div>
                <div className="report-controls">
                    <div className="control-group">
                        <label htmlFor="date-input">Periodo:</label>
                        {activeTab === 'Diaria' && <input type="date" id="date-input" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />}
                        {activeTab === 'Semanal' && <select id="date-input" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)}>{generateWeekOptions()}</select>}
                        {activeTab === 'Mensual' && <select id="date-input" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)}>{generateMonthOptions()}</select>}
                        {activeTab === 'Anual' && <select id="date-input" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)}>{generateYearOptions()}</select>}
                    </div>
                    
                    {/* 4. HIDE COMPSET SELECTOR IF RESTRICTED */}
                    {!isRestrictedUser && (
                        <div className="control-group">
                            <label htmlFor="compset-selector">Compset:</label>
                            <select id="compset-selector" value={selectedCompsetId} onChange={(e) => setSelectedCompsetId(e.target.value)} disabled={!compsets.length}>
                                {compsets.map(compset => (<option key={compset.comp_set_id} value={compset.comp_set_id}>{compset.name}</option>))}
                            </select>
                        </div>
                    )}
                </div>
            </section>
            <section className="info-entry-section">
                {loading && <p>Cargando reporte...</p>}
                {error && <p className="error-message">Error: {error}</p>}
                {!loading && !error && reportData && (
                    <div className="data-table-container">
                        <table className="kpi-table">
                            <thead>
                                {renderHeader()}
                                {renderSubHeader()}
                            </thead>
                            {renderTableBody()}
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
}

export default ReporteKPI;